import { useEffect, useState, useRef } from 'react'
import { advancedApi, type HighlightsResponse, type PreviewTable } from '../api'
import PDFViewerWithHighlights from './PDFViewer'
import { Maximize2, AlertTriangle } from 'lucide-react'

interface SplitScreenPreviewProps {
  documentId: string
  pdfUrl: string
  tables: PreviewTable[]
  columns: Array<{ id: string; column_name: string }>
  selectedIds: string[]
  toggle: (id: string) => void
  showFullPreviewButton?: boolean
}

export default function SplitScreenPreview({
  documentId,
  pdfUrl,
  tables,
  columns,
  selectedIds,
  toggle,
  showFullPreviewButton = true,
}: SplitScreenPreviewProps) {
  const [highlights, setHighlights] = useState<HighlightsResponse['highlights']>([])
  const [loading, setLoading] = useState(true)
  const [activeTableIndex, setActiveTableIndex] = useState<number | null>(null)
  const tableContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    advancedApi
      .getHighlights(documentId)
      .then((res) => {
        setHighlights(res.data.highlights)
      })
      .catch((err) => {
        console.error('Failed to load highlights:', err)
        // Highlights are optional, don't show error toast
      })
      .finally(() => setLoading(false))
  }, [documentId])

  const handleHighlightClick = (highlight: any) => {
    setActiveTableIndex(highlight.metadata?.table_index || 0)
    
    // Scroll to corresponding table
    if (tableContainerRef.current) {
      const tableElement = tableContainerRef.current.querySelector(
        `[data-table-index="${highlight.metadata?.table_index}"]`
      )
      if (tableElement) {
        tableElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }

  const handlePageChange = (page: number) => {
    // Filter tables for current page
    const pageTables = tables.filter((t) => t.page === page)
    if (pageTables.length > 0) {
      setActiveTableIndex(pageTables[0].table_index)
    }
  }

  const selectedCount = selectedIds.length

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        Loading split-screen view...
      </div>
    )
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left Panel: PDF Viewer */}
      <div className="w-1/2 border-r border-gray-200">
        <PDFViewerWithHighlights
          pdfUrl={pdfUrl}
          highlights={highlights}
          onHighlightClick={handleHighlightClick}
          onPageChange={handlePageChange}
        />
      </div>

      {/* Right Panel: Data Table */}
      <div className="w-1/2 overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-gray-900">Extracted Data</h2>
              <p className="text-sm text-gray-600">
                {tables.length} table{tables.length > 1 ? 's' : ''} detected • {selectedCount} columns selected
              </p>
            </div>
            {showFullPreviewButton && (
              <button
                onClick={() => window.open(`/documents/${documentId}/preview`, '_blank')}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
              >
                <Maximize2 className="w-4 h-4" />
                <span>Full Preview</span>
              </button>
            )}
          </div>
        </div>

        {/* Tables */}
        <div 
          ref={tableContainerRef}
          className="flex-1 overflow-auto p-4 bg-gray-50"
        >
          <div className="space-y-6">
            {tables.map((table, tableIdx) => (
              <div
                key={table.id || tableIdx}
                data-table-index={table.table_index}
                className={`bg-white border rounded-lg overflow-hidden ${
                  activeTableIndex === table.table_index
                    ? 'border-purple-500 ring-2 ring-purple-200'
                    : 'border-gray-200'
                }`}
              >
                {/* Table header */}
                <div className="px-4 py-2 bg-gray-100 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      Table {table.table_index} (Page {table.page})
                    </span>
                    <span className="text-xs text-gray-500">
                      {table.row_count} rows × {table.column_count} columns
                    </span>
                  </div>
                </div>

                {/* Column selection */}
                <div className="p-3 border-b border-gray-200 bg-white">
                  <div className="flex flex-wrap gap-2">
                    {columns.map((column) => (
                      <button
                        key={column.id}
                        onClick={() => toggle(column.id)}
                        className={`px-3 py-1.5 rounded-lg text-sm border transition-colors arabic-text ${
                          selectedIds.includes(column.id)
                            ? 'border-brand-blue bg-brand-light text-brand-blue'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {column.column_name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Data preview */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        {table.columns.map((col, colIdx) => (
                          <th
                            key={colIdx}
                            className="px-3 py-2 text-right text-xs font-medium text-gray-700 arabic-text whitespace-nowrap"
                          >
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {table.rows.slice(0, 10).map((row, rowIdx) => (
                        <tr key={rowIdx} className="hover:bg-gray-50">
                          {row.map((cell, cellIdx) => (
                            <td
                              key={cellIdx}
                              className="px-3 py-2 text-right text-gray-900 arabic-text whitespace-nowrap"
                            >
                              {cell || ''}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {table.row_count > 10 && (
                    <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 border-t border-gray-200">
                      Showing 10 of {table.row_count} rows. Export to see all data.
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {tables.length === 0 && (
            <div className="flex items-center justify-center h-48 text-gray-500">
              <div className="text-center">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
                <p>No tables detected in this document.</p>
                <p className="text-sm mt-1">Try running Advanced OCR for better detection.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
